import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import classNames from 'classnames';
import { InlineNotification } from '@carbon/react';
import { useStore } from '@tanstack/react-store';

import { NextCancelled, useWizardStep } from 'components/full-page-wizard';
import { DatePicker } from 'components/date-picker';
import { useBlobDownload } from 'lib/hooks/useBlobDownload';

import { packageSourceEntitySearchAtom } from '../atoms';
import { usePackageWizardCtx } from '../context';
import { useLargeTableConfirmedSync } from '../draft-build-hooks';
import { useAssetListSource } from '../hooks';
import { PackageEntityTable } from '../package-entity-table';
import type {
  PackageFormInstance,
  SourceSelectionChange,
  SourceType,
} from '../types';
import {
  applySourceSelection,
  asOfDateAfterSourceSwitch,
  baselineAcquirers,
  canAdvanceStep0,
  changeMapQueryFilters,
  createSourceOptions,
  LargeTableRefusal,
  makeDateClearHandler,
  markDraftTouched,
  pickedDate,
  step0Scope,
} from '../utils';
import { AddTractsByList } from './add-tracts-by-list';
import { AssetListPanel } from './asset-list-panel';
import { CloneNotes } from './clone-notes';
import { LargeTableModal } from './large-table-modal';
import type { MapQueryFiltersChange } from './map-query-source/types';
import { MapQuerySource } from './map-query-source/map-query-source';
import { EMPTY_MAP_QUERY_FILTERS } from './map-query-source/utils';
import { SourcePackageTable } from './source-package-table';
import { SourceDropdown } from './source-dropdown';
import { SourceTiles } from './source-tiles';
import styles from './select-source-step.module.scss';

const EMPTY_IDS: Set<number> = new Set();

/** Props for {@link SelectSourceStep}. */
export interface SelectSourceStepProps {
  /**
   * Creating a package: the "Create Package from" dropdown offers Map Query
   * (while its flag is on), Asset List and Existing Package. Editing keeps the
   * From Entity and From Existing Package tiles. The only thing the step knows
   * about the page's mode.
   */
  creating?: boolean;
}

/**
 * The As of Date field's change handler, and a key that re-mounts it.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @param {string | null} asOfDate - The form's As of Date
 * @return {object} `key` for the field, its `onChange` and `onInputChange`
 */
const useAsOfDateField = (
  form: PackageFormInstance,
  asOfDate: string | null
) => {
  const [key, showKeptDate] = useReducer((count: number) => count + 1, 0);
  /**
   * Idempotent — skips the write when the next value equals the current
   * (Carbon DatePicker re-fires onChange on reopen) — and marks the draft
   * touched only when the value actually moves, so reopening the picker
   * doesn't spuriously flip the flag. A date the field cannot show back is
   * ignored, and the key moves so the field re-mounts showing the date kept
   * (pickedDate).
   *
   * @param {Date[]} dates - Carbon DatePicker selection
   */
  const onChange = (dates: Date[]) => {
    const next = pickedDate(dates);
    if (next === undefined) {
      showKeptDate();
      return;
    }
    if (next === asOfDate) return;
    form.setFieldValue('asOfDate', next);
    markDraftTouched(form);
  };
  const onInputChange = makeDateClearHandler(form, 'asOfDate', asOfDate, () =>
    markDraftTouched(form)
  );
  return { key, onChange, onInputChange };
};

/**
 * Step 0 — Select Source. Collects the as-of date, the source type, and one
 * or more source rows from whichever table the type selects — or, on the
 * asset list, the pasted and uploaded Asset Names, checked as they change, or,
 * on Map Query, the filters of its panel. Creating, Map Query and Existing
 * Package take an optional list beside them ("Add tracts by list"), and a Next
 * above the large-table size asks before it builds.
 *
 * Sync with the BE draft is delegated to `api.upsertDraft` (POST on first
 * sync, PATCH afterward) — this step doesn't know whether it's running in
 * create or edit mode. Pusher subscription, materialization wait, and
 * `draftId` writeback all live in the api closure; the step just `await`s.
 *
 * Skip-on-clean: TanStack form's `fieldMeta.isDirty` tracks whether
 * `sources` / `asOfDate` differ from the form's defaults. After every
 * successful upsert the api clears those bits, so back-navigating to Step 0
 * and clicking Next without changing anything is a true no-op (no PATCH
 * fires, no 422 from the BE's `at_least_one_change` validator).
 *
 * @param {SelectSourceStepProps} props - Whether a package is being created
 * @return {JSX.Element} Step 0 content
 */
export const SelectSourceStep: FC<SelectSourceStepProps> = ({
  creating = false,
}) => {
  const { form, api } = usePackageWizardCtx();
  const { setNextEnabled, setOnNext } = useWizardStep();

  const sourceType = useStore(form.store, (s) => s.values.sourceType);
  const sources = useStore(form.store, (s) => s.values.sources);
  const asOfDate = useStore(form.store, (s) => s.values.asOfDate);
  const mapQueryFilters = useStore(form.store, (s) => s.values.mapQuery);
  const assetList = useAssetListSource(form);
  // The Map Query page reports how many assets its current filters return.
  const [mapQueryTotal, setMapQueryTotal] = useState<number | null>(null);
  const canProceed = useStore(form.store, (s) =>
    canAdvanceStep0(s.values, assetList.readiness, mapQueryTotal)
  );
  const sourceOptions = useMemo(() => createSourceOptions(), []);

  // Locally-owned banner, cleared on the next successful sync — the upsert
  // atom's loadable carries the same state but is global and would persist a
  // stale message across mounts.
  const [draftError, setDraftError] = useState<string | null>(null);
  const asOfDateField = useAsOfDateField(form, asOfDate);

  // Next's draft sync, with the large-table warning it may ask first.
  const { sync: syncDraft, warning: largeTable } =
    useLargeTableConfirmedSync(api);

  // Server-side CSV export of the entities table. The BE emits a
  // `Content-Disposition` filename via `send_data`; the supplied filename
  // is only a fallback. Filters aren't passed in the body — same shape
  // as the assets/slots exporters on Step 1.
  const { triggerDownload: handleEntityExport } = useBlobDownload({
    url: '/queries/entities/export',
    filename: 'entities.csv',
  });

  useEffect(() => {
    setNextEnabled(canProceed);
  }, [canProceed, setNextEnabled]);

  const handleNext = useCallback(async () => {
    const scope = step0Scope(
      form.state.values,
      assetList.summary.matched.map(({ name }) => name)
    );
    if (scope === null) return;
    // Whether to send anything is `api.upsertDraft`'s call (`step0DraftWrite`
    // compares the dirty Step 0 fields, which it clears after a successful
    // sync, and the scope last sent), so an unchanged Next sends nothing, on
    // the asset list too, except after a failed build of the list, which sends
    // it again.
    try {
      await syncDraft(scope);
      setDraftError(null);
    } catch (err) {
      // A large table the user chose not to build is no error: the wizard just
      // stays on Select Source, as for any Next that does not go through, unmarked.
      const cancelled = err instanceof LargeTableRefusal;
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setDraftError(cancelled ? null : message);
      throw cancelled ? new NextCancelled() : err;
    }
  }, [form, assetList.summary, syncDraft]);

  useEffect(() => {
    setOnNext(handleNext);
  }, [setOnNext, handleNext]);

  /**
   * Handle a source change, from the dropdown or the tiles. The sources stay
   * mutually exclusive, so switching clears the current selection, and the
   * acquirers it pre-populated go back to the ones the form opened with. No-op on
   * the draft-touched flag when nothing was picked. The asset list goes too — its
   * text, file, check answers and any upload in flight — and Map Query's location
   * ticks and map filter, so coming back starts each afresh. Creating, the As of
   * Date follows the source: today for Existing Package, none for Map Query.
   *
   * @param {SourceType} next - Newly selected source
   */
  const handleSourceChange = (next: SourceType) => {
    if (next === form.state.values.sourceType) return;
    form.setFieldValue('sourceType', next);
    if (form.state.values.sources.length > 0) {
      form.setFieldValue('sources', []);
      form.setFieldValue('acquiringEntities', baselineAcquirers(form));
      markDraftTouched(form);
    }
    assetList.clear();
    changeMapQueryFilters(form, () => EMPTY_MAP_QUERY_FILTERS);
    if (creating) {
      const date = asOfDateAfterSourceSwitch(next, form.state.values.asOfDate);
      if (date !== form.state.values.asOfDate) {
        form.setFieldValue('asOfDate', date);
        markDraftTouched(form);
      }
    }
  };

  /**
   * Apply a selection delta from one of the source tables, or Map Query's entity
   * ticks. The rule itself is shared with the header's tag set — see
   * `applySourceSelection`, which reads the live form value rather than the
   * rendered one so a burst of toggles can't drop an update.
   *
   * @param {SourceType} type - Which source reported the change
   * @param {SourceSelectionChange} change - Rows to add / ids to drop
   */
  const applyChange = useCallback(
    (type: SourceType, change: SourceSelectionChange) =>
      applySourceSelection(form, type, change),
    [form]
  );

  const handleEntitySelectionChange = useCallback(
    (change: SourceSelectionChange) => applyChange('entity', change),
    [applyChange]
  );
  const handlePackageSelectionChange = useCallback(
    (change: SourceSelectionChange) => applyChange('package', change),
    [applyChange]
  );
  const handleMapQueryEntitiesChange = useCallback(
    (change: SourceSelectionChange) => applyChange('mapQuery', change),
    [applyChange]
  );
  const handleMapQueryFiltersChange = useCallback<MapQueryFiltersChange>(
    (update) => changeMapQueryFilters(form, update),
    [form]
  );

  const selectedIds = useMemo(
    () => new Set(sources.map((source) => source.id)),
    [sources]
  );
  const selectedEntityIds = sourceType === 'entity' ? selectedIds : EMPTY_IDS;
  const selectedPackageIds = sourceType === 'package' ? selectedIds : EMPTY_IDS;

  const entityMultiSelect = useMemo(
    () => ({
      selectedIds: selectedEntityIds,
      onSelectionChange: handleEntitySelectionChange,
      active: sourceType === 'entity',
    }),
    [selectedEntityIds, handleEntitySelectionChange, sourceType]
  );

  return (
    <div className={styles.step}>
      {draftError !== null && (
        <InlineNotification
          kind="error"
          title="Couldn't sync package draft"
          subtitle={draftError}
          hideCloseButton
          lowContrast
          className={styles.errorNotification}
        />
      )}

      <div className={creating ? styles.controls : styles.dateRow}>
        {creating && (
          <SourceDropdown
            options={sourceOptions}
            selected={sourceType}
            onChange={handleSourceChange}
          />
        )}
        <DatePicker
          key={asOfDateField.key}
          id="package-wizard-as-of-date"
          labelText="Select As of Date *"
          value={asOfDate ?? undefined}
          onChange={asOfDateField.onChange}
          onInputChange={asOfDateField.onInputChange}
          autoComplete="off"
        />
      </div>

      {creating && sourceType === 'package' && <CloneNotes />}
      {creating && (sourceType === 'mapQuery' || sourceType === 'package') && (
        // Keyed by the source, so a switch of source opens it collapsed again.
        <AddTractsByList key={sourceType} {...assetList.panel} />
      )}
      {!creating && (
        <>
          <SourceTiles selected={sourceType} onChange={handleSourceChange} />
          {/* Both tables stay mounted across tile flips — TanStack state
              (sort, column visibility/order/pinning, scroll position,
              search input) lives inside DataTable's internal store and is
              lost on unmount. Toggling visibility via CSS preserves it. */}
          <div
            className={classNames(styles.tableWrapper, {
              [styles.tableWrapperHidden]: sourceType !== 'entity',
            })}
          >
            <PackageEntityTable
              searchAtom={packageSourceEntitySearchAtom}
              multiSelect={entityMultiSelect}
              onExport={handleEntityExport}
            />
          </div>
        </>
      )}

      <div
        className={classNames(styles.tableWrapper, {
          [styles.tableWrapperHidden]: sourceType !== 'package',
        })}
      >
        <SourcePackageTable
          selectedIds={selectedPackageIds}
          onSelectionChange={handlePackageSelectionChange}
          active={sourceType === 'package'}
        />
      </div>
      {sourceType === 'assetList' && <AssetListPanel {...assetList.panel} />}
      {sourceType === 'mapQuery' && (
        <div className={classNames(styles.tableWrapper, styles.mapQuery)}>
          <MapQuerySource
            asOfDate={asOfDate}
            entities={sources}
            onEntitiesChange={handleMapQueryEntitiesChange}
            filters={mapQueryFilters}
            onFiltersChange={handleMapQueryFiltersChange}
            listedTractIds={assetList.summary.tractIds}
            onResultsTotalChange={setMapQueryTotal}
          />
        </div>
      )}
      {largeTable && <LargeTableModal {...largeTable} />}
    </div>
  );
};
